import React, { ReactNode, useEffect, useState } from 'react';
import { useUserMe } from 'src/hooks/api/me';
import { useGetReservationFlowProduct } from 'src/hooks/api/reservation-flow/useGetReservationFlowProduct';
import { EmailVerifyTemplate } from 'src/templates/merchants/EmailVerifyTemplate';
import { PasswordVerifyTemplate } from 'src/templates/merchants/PasswordVerifyTemplate';
import { buildUrl } from 'src/utils/request-url';

type Props = {
  children: ReactNode;
  merchantPublicId: string;
  productPublicId: number;
  pageTitle: string;
};

type componentType = 'password' | 'email' | 'children' | null;

export const VerifiedRoot = ({
  children,
  merchantPublicId,
  productPublicId,
  pageTitle,
}: Props) => {
  const { isLoggedIn, meResponseData: loginUser, isUserLoading } = useUserMe();
  const { error: productDetailError, isLoading: isProductLoading } =
    useGetReservationFlowProduct({
      parameter: {
        merchant_public_id: merchantPublicId,
        product_public_id: productPublicId,
      },
    });

  const isRequiredPassword = productDetailError?.response?.status === 403;

  // NOTE: useEffectとトップレベルでの条件分岐が重複することになるため、state renderComponentを導入している。
  // NOTE: 例えば、useEffect、トップレベルの両方でisLoadingをチェックする必要があるなど。
  // NOTE: 上記のコードは可読性が低いためstateを導入しuseEffectでコンポーネントが決定するようにしている。
  const [renderComponent, setRenderComponent] = useState<componentType>(null);

  useEffect(() => {
    if (isUserLoading || isProductLoading) {
      return;
    }

    if (!isLoggedIn) {
      // NOTE: signinが外部リンクのためuseEffect & location.hrefでリダイレクトする必要がある。
      location.href = buildUrl(`/signin/user?return_to=${location.pathname}`);
      return;
    }

    if (!loginUser?.is_email_verified) {
      setRenderComponent('email');
    } else if (isRequiredPassword) {
      setRenderComponent('password');
    } else {
      setRenderComponent('children');
    }
  }, [
    isLoggedIn,
    isUserLoading,
    isProductLoading,
    loginUser,
    isRequiredPassword,
  ]);

  const component = () => {
    switch (renderComponent) {
      case 'password':
        return (
          <PasswordVerifyTemplate
            merchantPublicId={merchantPublicId}
            productPublicId={productPublicId}
            pageTitle={pageTitle}
          />
        );
      case 'email':
        return <EmailVerifyTemplate />;
      case 'children':
        return <>{children}</>;
      default:
        return null;
    }
  };

  return component();
};
